<template>
	<div :class="'egranty-table' + (compact ? ' compact-table' : '')">
		<v-toolbar class="egrantyTableToolbar blue mt-3 mb-3">
			<v-toolbar-title>{{ tableName }}</v-toolbar-title>
			<v-spacer></v-spacer>
		</v-toolbar>

		<hideable-columns-menu :columns="headers"
			@column-toggled="columnToggled"
			:column-visibilities="columnVisibility"
			v-if="hideableColumns"/>
		<div>
			<v-btn color="primary" class="ml-3 mb-3" @click="exportData" dark>
				<v-icon class="export-icon">save_alt</v-icon>
				Eksportuj
			</v-btn>
		</div>
		<div>
			<v-btn class="mb-2 ml-3" v-if="toolbarActionName !== '' && toolbarActionVisible"
				color="primary" dark
				@click="$emit('toolbarAction')">
				{{ toolbarActionName }}
			</v-btn>
		</div>

		<v-data-table
			:headers="computedHeaders"
			:items="computedItems"
			:hide-actions="hidePagination"
			item-key="name"
			class="elevation-1"
		>

			<template slot="headerCell" slot-scope="{ header }">
				<div :class="'column-header ' + getHeaderClass(header)">
					<template v-if="header.filterable">
						<v-text-field
							browser-autocomplete="off"
							v-model="searchModel[header.value]"
							append-icon="search"
							hide-details box clearable
							class="pa-0 mb-2 egranty-table-header-search"
							@click="eatEvent"
							v-if="header.type === 'TEXT' || header.type === 'NUMBER' || header.type === 'CURRENCY'"
							aria-label="label for text"
						></v-text-field>
						<egranty-three-state-switch
							v-model="searchModel[header.value]"
							@click="eatEvent"
							v-if="header.type === 'BOOLEAN'"
							aria-label="label for switch"
						></egranty-three-state-switch>
					</template>

					<v-tooltip bottom v-if="header.tooltip">
						<template v-slot:activator="{ on }">
							<span v-text="header.text" v-on="on" class="mr-3"/>
						</template>
						<span>{{header.tooltip}}</span>
					</v-tooltip>
					<span v-else v-text="header.text" class="mr-3"/>
				</div>
			</template>

			<template slot="items" slot-scope="{ item, index }">
				<tr>
					<template v-for="(header, headerIndex) in computedHeaders">
						<template v-if="header.editable">
							<td :class="getCellClass(header)" :key="'cell-' + headerIndex">
								<v-text-field v-model="item[header.value]"
									browser-autocomplete="off"
									@change="notifyItemUpdated(item, header.value)"
									v-if="header.type === 'TEXT'"
									aria-label="label for text">
								</v-text-field>
								<v-text-field v-model="item[header.value]"
									browser-autocomplete="off"
									@change="notifyItemUpdated(item, header.value)"
									mask="##################"
									v-if="header.type === 'NUMBER'"
									aria-label="label for text">
								</v-text-field>
								<egranty-number-input-field
									v-on:bl="notifyItemUpdated(item, header.value)"
									v-model="item[header.value]"
									:double-mechanism="false"
									v-if="header.type === 'CURRENCY'"
								>
								</egranty-number-input-field>
								<egranty-three-state-switch v-model="item[header.value]"
									@change="notifyItemUpdated(item, header.value)"
									v-if="header.type === 'BOOLEAN'">
								</egranty-three-state-switch>
							</td>
						</template>

						<td :class="getCellClass(header)" :key="'cell-' + headerIndex"
							v-else-if="!header.isActionsHeader && !header.editable">
							<template v-if="header.type === 'BOOLEAN'">
								<template v-if="item[header.value] === true">
									<v-icon>done</v-icon>
								</template>
								<template v-else-if="item[header.value] === false">
									<v-icon>clear</v-icon>
								</template>
								<template v-else>
									<v-icon v-if="agencyEvaluation(header.value)">{{ item.agencyEvaluationAssigned ?
										'help_outline' : '' }}
									</v-icon>
									<v-icon v-else-if="expertEvaluation(header.value)">{{ expertEvaluatorPresent(item,
										header.value) ? 'help_outline' : '' }}
									</v-icon>
									<v-icon v-else>help_outline</v-icon>
								</template>
							</template>
							<template v-else-if="header.type === 'CURRENCY'">
								{{ formatMoney(item[header.value]) }}
							</template>
							<template v-else-if="substantiveEvaluation(header.value)">
								<template v-if="substantiveEvaluatorPresent(item, header.value)">
									<template v-if="item[header.value]">{{ item[header.value].toString().replace(/\./g,
										",") }}
									</template>
									<template v-else>
										<v-icon>help_outline</v-icon>
									</template>
								</template>
								<template v-else>
									{{ item[header.value] ? item[header.value].toString().replace(/\./g, ",") :
										item[header.value] }}
								</template>
							</template>
							<template v-else>
								{{ item[header.value] && !isNaN(item[header.value]) ?
									item[header.value].toString().replace(/\./g, ",") :
									item[header.value] }}
							</template>
						</td>
					</template>

					<td v-if="hasActions && rowActionsVisible" class="text-xs-center">
						<v-layout row>
							<template v-for="(action, actionIndex) in rowActions">
								<egranty-icon-button
									small
									:key="'action-' + actionIndex"
									:icon="action.icon"
									:label="action.label"
									@click="$emit(action.name, item)"
									v-if="getVisibilityFunction(action)(item)"
									:disabled="getDisabledFunction(action)(item)"
									:color="getIconColor(action)"
								></egranty-icon-button>
							</template>
						</v-layout>
					</td>
				</tr>
				<tr v-if="hasFooterWithSums && index === computedItems.length - 1" class="footer-row">
					<template v-for="(header, headerIndex) in computedHeaders">
						<td :class="getCellClass(header) + ' footer-cell'" :key="'footer-cell-' + headerIndex">
							{{ header.summable ? formatMoney(header.computedSum) : "" }}
						</td>
					</template>
				</tr>
			</template>
		</v-data-table>
		<!--<pretty-j-s-o-n :json="computedItems"/>-->
	</div>
</template>

<script>
	import HideableColumnsMenu from "./egrantyTable/HideableColumnsMenu";
	import {Files} from "../utils/Files";
	import {Dates} from "../utils/Dates";
	import EgrantyIconButton from "./EgrantyIconButton";
	import EgrantyThreeStateSwitch from "./EgrantyThreeStateSwitch";
	import {Numbers} from "../utils/Numbers";
	import {Api} from "../api";
	import EgrantyNumberInputField from "./EgrantyNumberInputField";

	export default {

		name: "egrantyTable",
		components: {EgrantyNumberInputField, EgrantyIconButton, EgrantyThreeStateSwitch, HideableColumnsMenu},

		mixins: [Files, Dates, Numbers],


		data() {
			return {
				columnVisibility: null,
				searchModel: {},
			};
		},

		watch: {
			headers: function (newVal) {
				if (this.columnVisibility === null) {
					this.columnVisibility = newVal
						.reduce((obj, h) => {
							obj[h.value] = true;
							return obj;
						}, {});
				}

				this.searchModel = newVal
					.reduce((obj, header) => {
						if (header.type === "BOOLEAN") {
							obj[header.value] = "ALL";
						} else {
							obj[header.value] = "";
						}
						return obj;
					}, {});
			},
		},

		computed: {
			columnFilters() {
				return this.headers.map(
					header => {
						let ctx = this;
						switch (header.type) {
							case "NUMBER":
								return getNumberFilter(ctx, header.value);
							case "CURRENCY":
								return getNumberFilter(ctx, header.value);
							case "TEXT":
								return getTextFilter(ctx, header.value);
							case "BOOLEAN":
								return getBooleanFilter(ctx, header.value);
							default:
								return getTextFilter(ctx, header.value);
						}
					},
				);
			},

			computedHeaders() {
				let computedHeaders = [...this.headers];

				if (this.hasActions) {
					computedHeaders.push({
						text: "Akcje",
						width: "10%",
						value: "egranty-table-actions",
						align: "center",
						sortable: false,
						isActionsHeader: true,
					});
				}

				if (this.hideableColumns) {
					computedHeaders = computedHeaders.filter(h => this.columnVisibility[h.value]);
				}

				computedHeaders
					.filter(h => !h.type && !h.isActionsHeader)
					.forEach(h => this.$set(h, "type", "TEXT"));

				return computedHeaders;
			},

			computedItems() {
				let result = [...this.items];
				result = JSON.parse(JSON.stringify(result));	// deepcopy
				for (let i = 0; i < result.length; ++i) {
					this.$set(result[i], "egrantyTableIndex", i);
				}
				let allFilters = [...this.rowFilters, ...this.columnFilters];
				allFilters.forEach(f => result = result.filter(f));
				this.computedHeaders.forEach(
					header => {
						this.$set(header, "computedSum", 0);
						result.forEach(item => {
							if (header.postProcessor) {
								this.$set(item, header.value, header.postProcessor(item));
							}
							if (header.summable) {
								header.computedSum = header.computedSum + (parseInt(item[header.value]) || 0);
							}
						});
					},
				);

				return result;
			},

			hasActions() {
				return this.rowActions.length > 0;
			},

			hasFooterWithSums() {
				return this.headers.some(h => h.summable);
			},
		},

		methods: {
			eatEvent(e) {
				e.stopPropagation();
			},

			getVisibilityFunction(action) {
				return typeof action.isVisible === "function" ? action.isVisible : () => true;
			},

			getDisabledFunction(action) {
				return typeof action.isDisabled === "function" ? action.isDisabled : () => false;
			},

			getIconColor(action) {
				return action.icon === 'delete' ? 'lightred' : 'primary';
			},

			notifyItemUpdated(item, key) {
				this.$emit("item-updated", item, key);
			},

			columnToggled(columnKey, newVisibility) {
				this.columnVisibility[columnKey] = newVisibility;
			},

			notBooleanColumn (key) {
				return !key.includes('substantiveEvaluationsRecommendings');
			},

			getHeaderClass(header) {
				let cssClass = "";
				switch (header.type) {
					case "TEXT":
						cssClass = "text-column-header";
						break;
					case "NUMBER":
						cssClass = "number-column-header";
						break;
					case "CURRENCY":
						cssClass = "number-column-header";
						break;
					case "BOOLEAN":
						cssClass = "boolean-column-header";
						break;
				}
				if (header.headerClass) {
					cssClass += " " + header.headerClass;
				}
				return cssClass;
			},

			getCellClass(header) {
				let cssClass = "";
				switch (header.type) {
					case "TEXT":
						cssClass = "text-xs-left";
						break;
					case "NUMBER":
						cssClass = "text-xs-left";
						break;
					case "CURRENCY":
						cssClass = "text-xs-right";
						break;
					case "BOOLEAN":
						cssClass = "text-xs-center";
						break;
				}
				if (header.cellClass) {
					cssClass += " " + header.cellClass;
				}
				return cssClass;
			},

			substantiveEvaluation (headerValue) {
				return headerValue.startsWith('substantiveEvaluationsPointSums');
			},

			agencyEvaluation (headerValue) {
				return headerValue.startsWith('agencyEvaluation');
			},

			expertEvaluation (headerValue) {
				return headerValue.startsWith('expertEvaluations');
			},

			substantiveEvaluatorPresent (item, headerValue) {
				let evaluationIndex = headerValue.substring(31);
				let found = this.computedItems.find(computed => computed.id === item.id);
				return found['committeeNames' + evaluationIndex];
			},

			expertEvaluatorPresent (item, headerValue) {
				let evaluationIndex = headerValue.substring(17);
				return evaluationIndex <= item.expertEvaluationCount;
			},

			exportData() {
				let itemsToExport = this.computedItems;
				let fieldName = this.exportSortedByFieldName;
				if (fieldName) {
					itemsToExport.sort(function (x, y) {
						return  parseInt(x[fieldName]) - parseInt(y[fieldName]);
					});
				}

				let data = {
					headers: this.computedHeaders
						.filter(h => !h.isActionsHeader)
						.map(h => {
							return {
								caption: h.text,
								key: h.value,
								type: h.type,
							};
						}, {}),
					rows: itemsToExport,
				};
				Api.post(this, "/data-export/xls", data,
					success => {
						let filename = "egranty_export_" + this.getFilenameTimestamp() + ".xlsx";
						this.$messagesBus.$emit("success", "Eksport danych z tabeli wykonano do pliku " + filename,
							success);
						this.downloadBlobResponse(success, filename);
					}, error => {
						this.$messagesBus.$emit("error", "Eksport nieudany", error);
					},
					{responseType: "blob"});
			},

			removeEmptyTableLoading () {
				var el = document.querySelector('.v-datatable__progress');
				el.parentNode.removeChild(el);
			}
		},

	
		mounted() {
			this.removeEmptyTableLoading();
		},


		props: {
			/**
			 * Kopiowane, filtrowane i przekazywane do v-data-table:items.
			 */
			items: {type: Array, required: true, default: () => []},

			/**
			 * Kopiowane i przekazywane do v-data-table:headers. Jeśli tabela zawiera akcje,
			 * dodawany jest nagłówek z akcjami.
			 *
			 * Dodatkowe pola:
			 * - postProcessor: (Function(item)) - funkcja do zmiany wartości danego pola dla item
			 * - filterable: (Boolean) - czy kolumna może być filtrowana
			 * - editable: (Boolean) - czy pola można edytować
			 * - summable: (Boolean) - czy kolumna będzie zawierała sumę z komórek w ostatnim wierszu tabeli
			 * - type: (String: TEXT, NUMBER, CURRENCY, BOOLEAN) - wpływa na metodę filtrowania i sortowania
			 * - cellClass: (String) - klasa css dodawana do każdej komórki
			 * - headerClass: (String) - klasa css dodawana do każdego nagłówka
			 */
			headers: {type: Array, required: true, default: () => []},
			rowActions: {type: Array, default: () => []},
			rowActionsVisible: {type: Boolean, default: true},
			toolbarActionName: {type: String, default: ""},
			toolbarActionVisible: {type: Boolean, default: true},
			hidePagination: {type: Boolean, default: false},
			tableName: {type: String, default: ""},
			rowFilters: {type: Array, default: () => []},
			hideableColumns: {type: Boolean, default: false},
			exportEnabled: {type: Boolean, default: false},
			exportSortedByFieldName: {type: String, default: null},
			compact: {type: Boolean, default: false},
		},
	};

	function getTextFilter(ctx, key) {
		return getFilter(ctx, key, (v, q) => {
			if (v && q) {
				return v.toUpperCase().indexOf(q.toUpperCase()) >= 0;
			} else {
				return null;
			}
		});
	}

	function getNumberFilter(ctx, key) {
		return getFilter(ctx, key, (v, q) => v.toString().startsWith(q));
	}

	function getBooleanFilter(ctx, key) {
		if (ctx.agencyEvaluation(key)) {
			return getAgencyEvaluationFilter(ctx, key);
		}
		if (ctx.expertEvaluation(key)) {
			return getExpertEvaluationFilter(ctx, key);
		}
		return getFilter(ctx, key, (v, q) => {
			return (v === true ? "PASSED" :
				v === false ? "NOT_PASSED" :
				"UNKNOWN") === q;
		});
	}

	function getAgencyEvaluationFilter (ctx, key) {
		return function (item) {
			if (ctx.searchModel[key] === "PASSED") {
				return item[key] === true;
			} else if (ctx.searchModel[key] === "NOT_PASSED") {
				return item[key] === false;
			} else if (ctx.searchModel[key] === "UNKNOWN") {
				return item.agencyEvaluationAssigned && item[key] === null;
			} else return true;
		};
	}

	function getExpertEvaluationFilter (ctx, key) {
		return function (item) {
			if (ctx.searchModel[key] === "PASSED") {
				return item[key] === true;
			} else if (ctx.searchModel[key] === "NOT_PASSED") {
				return item[key] === false;
			} else if (ctx.searchModel[key] === "UNKNOWN") {
				return ctx.expertEvaluatorPresent(item, key) && item[key] === null;
			} else return true;
		};
	}

	function getFilter(ctx, key, testFunction) {
		return function (item) {
			if (ctx.searchModel[key] === "" || ctx.searchModel[key] === null ||
				ctx.searchModel[key] === undefined || ctx.searchModel[key] === "ALL") {
				return true;
			}

			if (ctx.notBooleanColumn(key) && !item[key] && ctx.searchModel[key]) {
				return false;
			}

			return testFunction(item[key], ctx.searchModel[key]);
		};
	}
</script>

<style>
	.v-text-field--box > .v-input__control > .v-input__slot {
		height: 24px;
		line-height: 24px;
		min-height: 24px;
	}

	.v-text-field--box > .v-input__control > .v-input__slot input {
		padding: 0px;
		margin: 0px;
		font-size: 0.7em;
	}

	.v-text-field.v-text-field--enclosed .v-input__append-inner {
		margin-top: 0px;
	}

	.v-text-field.v-text-field--enclosed > .v-input__control > .v-input__slot {
		padding: 0 5px;
	}

	.v-text-field .v-input__append-inner {
		padding-left: 0px;
	}

	.v-datatable thead th.column.sortable.active.desc .v-input .v-icon {
		-webkit-transform: none;
		transform: none;
	}

	.v-datatable thead th.column.sortable .boolean-column-header button i.v-icon {
		opacity: 1;
		color: inherit;
	}

	.boolean-column-header button {
		display: block;
		margin-left: auto;
		height: 26px;
		width: 26px;
		margin-right: auto;
		margin-top: 0;
	}

	tr.footer-row {
		background: lightgray;
		font-weight: bold;
	}

	.compact-table .v-text-field.v-text-field--enclosed .v-input__append-inner {
		height: 18px;
		width: 18px;
		min-width: 18px;
	}

	.compact-table table.v-table tbody td {
		font-weight: 400;
		font-size: 14px;
		padding: 0 4px;
	}

	.compact-table table.v-table thead th {
		font-weight: 400;
		font-size: 10px;
		padding: 0 4px;
		vertical-align: top;
	}

	.compact-table table.v-table tbody td input {
		font-weight: 400;
		font-size: 10px;
	}

	.compact-table .column-header span {
		display: inline-block;
		white-space: normal;
	}

	.compact-table .text-column-header span {
		min-width: 130px;
	}

	.compact-table .number-column-header span {
		min-width: 70px;
	}

	.compact-table .boolean-column-header span {
		min-width: 70px;
	}

	.compact-table .column-header.header-index span {
		min-width: 40px;
	}

	.theme--dark .v-toolbar {
		background-color: #1B1B1B !important;
	}

	.cell-recommendedDotation .v-input .v-input__control .v-input__slot input {
		text-align: right;
		font-size: 14px;
	}


</style>
